import { ApiError } from '../api/client';
import { verifyOtp } from './otp';

/**
 * Forgot password — SIMULATED, like the code step in lib/auth/otp.ts.
 *
 * The flow is real (email → code → new password); the effects are not. No
 * email is sent, the code is the constant from lib/auth/otp.ts, and the "new
 * password" is checked against the rules below and then thrown away. Nothing
 * reaches a server, so the account's actual password does not change.
 *
 * With Microsoft 365 accounts the real reset belongs to Microsoft. When that
 * is connected, either point these functions at it or drop the page. Callers
 * depend only on the signatures.
 */

/** While true, the pages say out loud that nothing was really changed. */
export const RESET_SIMULATED = true;

export const PASSWORD_MAX = 128;

export interface PasswordRule {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

/** A proposal until the backend states its own; keep the two identical. */
export const PASSWORD_RULES: PasswordRule[] = [
  { id: 'length', label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { id: 'upper', label: 'An uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'A lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { id: 'digit', label: 'A number', test: (pw) => /\d/.test(pw) },
  { id: 'symbol', label: 'A symbol', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

const LATENCY_MS = 450;

function pause() {
  return new Promise<void>((resolve) => window.setTimeout(resolve, LATENCY_MS));
}

/**
 * Always resolves, for a known address and an unknown one alike — the real
 * endpoint must do the same, or the form tells a stranger who has an account.
 */
export async function requestReset(_email: string): Promise<void> {
  await pause();
}

export const verifyResetCode = verifyOtp;

export async function resetPassword(password: string): Promise<void> {
  await pause();
  if (!PASSWORD_RULES.every((rule) => rule.test(password))) {
    throw new ApiError('That password does not meet the rules.', 400);
  }
}
